<script lang="ts">
	//Svelte hooks
	import { onMount, onDestroy } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { search_shelly_wifi_devices_android } from '../../../../services/native_container_requests_android';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { inclusionState, discoveredWiFiDevices } from 'src/stores/features/inclusion';
	import { deviceMainFilter } from 'src/stores/ui/filters';
	//Helpers
	import { capFirstChar } from '../../../../helpers/utilities';
	import { moduleTransformId } from 'src/helpers/interface_helpers';
	//Components
	//Design system components
	import {
		CardGrid,
		SelectCard,
		Loader,
		Text,
		Input,
		Button,
		Select,
	} from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let inclusionData;

	$: isSearchingForDevices =
		($inclusionState?.id == 10 && $inclusionState?.type === 'status') ||
		($inclusionState?.id == 18 && $inclusionState?.type === 'status');

	const scanForDevices = async () => {
		discoveredWiFiDevices.set([]);
		search_shelly_wifi_devices_android();
	};

	onMount(async () => {
		scanForDevices();
	});

	onDestroy(() => {
		deviceMainFilter.set('');
	});

	const handleDeviceSelect = (device) => {
		inclusionData.selected = device;
	};

	interface SelectOptionPropsTypeExtended extends SelectOptionPropsType {
		translationDefault?: string;
		value: string;
	}

	const defaultAllDiscoveredFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_all_discovered',
		value: '',
		translationDefault: 'All devices',
	};

	const defaultNewDevicesFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_new_discovered',
		value: 'new',
		translationDefault: 'New devices',
	};

	const defaultUserOwnedFilterOption: SelectOptionPropsTypeExtended = {
		title: 'filter_mine_discovered',
		value: 'owned',
		translationDefault: 'My devices',
	};

	const ownedOptions: SelectOptionPropsTypeExtended[] = [
		{
			...defaultNewDevicesFilterOption,
			title: $format(defaultNewDevicesFilterOption.title, {
				default: defaultNewDevicesFilterOption.translationDefault,
			}),
		},
		{
			...defaultUserOwnedFilterOption,
			title: $format(defaultUserOwnedFilterOption.title, {
				default: defaultUserOwnedFilterOption.translationDefault,
			}),
		},
		{
			...defaultAllDiscoveredFilterOption,
			title: $format(defaultAllDiscoveredFilterOption.title, {
				default: defaultAllDiscoveredFilterOption.translationDefault,
			}),
		},
	];

	// initiate to this value so as to transalte it via $format
	let devicesOwnedFilter = {
		...defaultNewDevicesFilterOption,
		title: $format(defaultNewDevicesFilterOption.title, {
			default: defaultNewDevicesFilterOption.translationDefault,
		}),
	};

	const filterByOwnership = (list, filter) => {
		// First filter by search term if present
		const searchTerm = $deviceMainFilter.toLowerCase().trim();
		let filteredBySearch = list;

		// Only apply search filtering if there's something to search for
		if (searchTerm !== '') {
			filteredBySearch = list.filter((d) => {
				// Search by name
				const deviceName = ('Shelly ' + (d.name || '')).toLowerCase();
				if (deviceName.includes(searchTerm)) return true;

				// Search by device ID/MAC
				const deviceID = d.id ? d.id.toLowerCase() : '';
				if (deviceID.includes(searchTerm)) return true;

				// Search by display ID
				const displayID = d.displayID ? d.displayID.toLowerCase() : '';
				if (displayID.includes(searchTerm)) return true;

				return false;
			});
		}
		return filteredBySearch.filter((d) => {
			const deviceID =
				d.gen == 'GBLE'
					? d.id
					: // Devices with jti have a different ID format and need to be transformed
						d?.jti
						? moduleTransformId(d.id, d?.jti)
						: d.id.toLowerCase();
			const jumbledID = typeof d.jumbledID !== 'undefined' ? d.jumbledID.toLowerCase() : '';
			d.displayID = deviceID;
			/**
			 * Logic to filter out devices that are already in the account or ones that are missing
			 * Devices int he app storage list are stored via lower case of the device Wi-Fi Mac Address.
			 * So to display them made easy to match we case the correct device id to lower case.
			 *
			 * This logic also contains the backwards compatability (jumbledID) for jumbled Mac returned via Bluetooth scan
			 * that is returned by Gen.2 devices with firmware versions between 0.14.** to 1.0.3
			 */
			if (
				filter == 'new' &&
				typeof $devices[deviceID] === 'undefined' &&
				typeof $devices[jumbledID] === 'undefined'
			) {
				d.displayID = deviceID;
				return d;
			} else if (filter == 'owned') {
				if (typeof $devices[deviceID] !== 'undefined') {
					d.displayID = deviceID;
					return d;
				} else if (typeof $devices[jumbledID] !== 'undefined') {
					d.displayID = jumbledID;
					return d;
				}
			} else if (filter == '') {
				return d;
			}
		});
	};

	$: searchTerm = $deviceMainFilter;

	$: discoveredDevices = filterByOwnership(
		$discoveredWiFiDevices,
		devicesOwnedFilter.value,
		searchTerm,
	);

	const emptyFilteredListTexts = {
		new: $format('inclusion_ble_empty_filter_new', {
			default: 'No new devices found nearby.',
		}),
		owned: $format('inclusion_ble_empty_filter_owned', {
			default: 'No owned devices found nearby.',
		}),
	};

	$: emptyFilteredListText = emptyFilteredListTexts[devicesOwnedFilter.value];
</script>

<Text element="h2" class="mb-s">
	{$format('via_ap_scan', {
		default: 'Add via Wi-Fi (AP Scan)',
	})}
</Text>

{#if isSearchingForDevices}
	<TransitionWrapper>
		<svelte:fragment>
			<Text class="mb-s" styleType="body-small">
				{$format('inclusion_wifi_searching', {
					default: 'Searching for nearby Wi-Fi devices.',
				})}
			</Text>
			<Loader size="l" class="d-flex justify-content-center mb-m" />
		</svelte:fragment>
	</TransitionWrapper>
{:else if $discoveredWiFiDevices.length == 0}
	<TransitionWrapper>
		<svelte:fragment>
			<Text class="mb-s" styleType="body-small">
				{$format('no_devices_found', {
					default: 'No devices found ',
				})}
			</Text>
		</svelte:fragment>
	</TransitionWrapper>
{:else}
	<TransitionWrapper>
		<svelte:fragment>
			<Button
				class="mb-m"
				variant="secondary"
				icon="fas fa-sync"
				size="m"
				width="full"
				on:click={scanForDevices}
				>{capFirstChar($format('button_scan_again', { default: 'scan again' }))}</Button
			>
			<Text class="mb-s" styleType="body-small">
				{$format('inclusion_ble_list', {
					default: 'Select the device you want to include.',
				})}
			</Text>
			<Select
				id="device-owned-filter"
				aria-labelledby="device-owned-filter-01"
				class="width-lg-l width-md-l width-sm-full mb-s"
				bind:value={devicesOwnedFilter}
				options={ownedOptions}
				icon="fas fa-filter"
				label={$format(defaultNewDevicesFilterOption.title, {
					default: defaultNewDevicesFilterOption.translationDefault,
				})}
				on:change
			/>
			<Input
				id="input-device-search"
				aria-label={$format('search', { default: 'search' })}
				type="text"
				label={capFirstChar($format('search', { default: 'search' }))}
				icon="fas fa-search"
				class="width-lg-l width-md-l width-sm-full mb-m"
				bind:value={$deviceMainFilter}
				disabled={discoveredDevices.length == 0 && $deviceMainFilter == ''}
			/>
			<!-- Alex: can be simplified by merging empty list descriptions, will do at a later time -->
			{#if discoveredDevices.length == 0 && $deviceMainFilter == ''}
				<Text class="mb-s " styleType="body-small">
					{emptyFilteredListText}
				</Text>
			{:else if discoveredDevices.length == 0 && $deviceMainFilter !== ''}
				<Text class="mb-s " styleType="body-small">
					{$format('inclusion_ble_empty_filter_search', {
						default: `No devices found while searching for: ${$deviceMainFilter}`,
						values: { searchValue: $deviceMainFilter },
					})}
				</Text>
			{:else}
				<CardGrid lg={290} sm={270} noPaddingBottom gridGap="s">
					{#each discoveredDevices as discoveredDevice}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							image={`images/device_images/${discoveredDevice.service_type || discoveredDevice.type}.png`}
							size="s"
							title={`${discoveredDevice.service_type ? '' : 'Shelly'} ${discoveredDevice.name}`}
							descriptiveText={`${discoveredDevice.displayID}`}
							variant={'radio'}
							selected={inclusionData.selected === discoveredDevice}
							on:click={() => handleDeviceSelect(discoveredDevice)}
						>
							<!-- <svelte:fragment slot="leftBadge">
						{#if discoveredDevice.gen === 'GBLE' && $advertisingBleDevices.indexOf(discoveredDevice.id) > -1}
							<TransitionWrapper>
								<Label
									icon={`fas fa-${
										discoveredDevice.type === 'SBBT-002C'
											? 'circle-dot'
											: 'door-open'
									}`}
									type="success"
									size="s"
									value={discoveredDevice.type === 'SBBT-002C'
										? 'clicked'
										: 'opened'}
								/>
							</TransitionWrapper>
						{/if}
					</svelte:fragment> -->
						</SelectCard>
					{/each}
				</CardGrid>
			{/if}
		</svelte:fragment>
	</TransitionWrapper>
{/if}
